/**
 * Estado REAL del número de WhatsApp dentro de Meta, leído de la configuración
 * persistida de la integración.
 *
 * Por qué existe: la card y la pantalla de configuración solo sabían si Plick tenía
 * credenciales guardadas ("CONECTADO"), no si Meta considera el número apto para
 * enviar. Un número en `status: PENDING` o en `platform_type: ON_PREMISE` deja pasar
 * el connect entero y luego rechaza cada template — que es exactamente el escenario
 * en el que el comercio ve verde y no le llega ni un mensaje al cliente.
 *
 * DEPENDENCIA DE BACKEND: hoy el connect de `metaCloudApiIntegration.js` consulta
 * estos campos a Graph pero NO los persiste en `configuration`. Mientras no lleguen,
 * `readPhoneNumberStatus` devuelve `null` y la UI simplemente no pinta la sección: la
 * ausencia de dato NUNCA se interpreta como problema (una advertencia falsa sería
 * peor que no mostrar nada).
 */

/** Estado del número tal como lo expone el nodo `phone_numbers` de Graph. */
export interface PhoneNumberStatus {
  /** `status` de Graph: CONNECTED, PENDING, FLAGGED, RESTRICTED, … */
  status: string | null;
  /** `platform_type` de Graph: CLOUD_API, ON_PREMISE, NOT_APPLICABLE. */
  platformType: string | null;
}

/** Único valor de `status` con el que el número puede enviar. */
export const HEALTHY_STATUS = 'CONNECTED';
/** Única plataforma que esta integración sabe usar. */
export const HEALTHY_PLATFORM_TYPE = 'CLOUD_API';

const STATUS_LABELS: Record<string, string> = {
  CONNECTED: 'Conectado',
  PENDING: 'Pendiente de registro',
  FLAGGED: 'Marcado por Meta',
  RESTRICTED: 'Restringido',
  RATE_LIMITED: 'Con límite de envío',
  BANNED: 'Bloqueado por Meta',
  MIGRATED: 'Migrado a otra cuenta',
  DELETED: 'Eliminado',
  UNVERIFIED: 'Sin verificar',
  UNKNOWN: 'Desconocido',
};

const PLATFORM_LABELS: Record<string, string> = {
  CLOUD_API: 'WhatsApp Cloud API',
  ON_PREMISE: 'On-Premise API',
  NOT_APPLICABLE: 'No aplica',
};

const STATUS_WARNINGS: Record<string, string> = {
  PENDING: 'El número aún no está registrado en WhatsApp Cloud API; no puede enviar mensajes.',
  FLAGGED:
    'Meta marcó el número por baja calidad de las conversaciones. Puede dejar de enviar si la calificación no mejora.',
  RESTRICTED: 'Meta restringió el envío desde este número. Revisa las notificaciones de tu cuenta en Meta Business.',
  RATE_LIMITED: 'Meta limitó el volumen de envío del número. Algunas notificaciones pueden no salir.',
  BANNED: 'Meta bloqueó el número. No se enviará ninguna notificación hasta resolverlo con el soporte de Meta.',
  MIGRATED: 'El número se migró a otra cuenta de WhatsApp Business. Vuelve a conectarlo para seguir enviando.',
  DELETED: 'El número ya no existe en tu cuenta de WhatsApp Business. Vuelve a conectar la integración.',
  UNVERIFIED: 'El número no está verificado en Meta. Completa la verificación para poder enviar mensajes.',
};

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};

/**
 * Lee el estado del número de la configuración persistida.
 *
 * @param {Record<string, unknown>} config Configuración de la integración.
 * @returns {PhoneNumberStatus | null} Estado, o `null` si el backend todavía no manda
 *   ninguno de los dos campos (la UI no debe pintar nada en ese caso).
 */
export const readPhoneNumberStatus = (config: Record<string, unknown>): PhoneNumberStatus | null => {
  const status = readString(config?.status);
  const platformType = readString(config?.platform_type);
  if (!status && !platformType) return null;
  return { status, platformType };
};

/**
 * ¿El número puede enviar mensajes? Un campo AUSENTE no cuenta como problema: solo se
 * juzga lo que el backend sí mandó.
 *
 * @param {PhoneNumberStatus} phoneStatus Estado leído de la configuración.
 * @returns {boolean} `true` si nada de lo conocido impide enviar.
 */
export const isPhoneNumberHealthy = ({ status, platformType }: PhoneNumberStatus): boolean =>
  (!status || status === HEALTHY_STATUS) && (!platformType || platformType === HEALTHY_PLATFORM_TYPE);

/**
 * Etiqueta legible del `status`.
 *
 * @param {string | null} status Valor crudo de Graph.
 * @returns {string} Etiqueta en español (o el valor crudo si es desconocido).
 */
export const getStatusLabel = (status: string | null): string => (status ? STATUS_LABELS[status] || status : '—');

/**
 * Etiqueta legible del `platform_type`.
 *
 * @param {string | null} platformType Valor crudo de Graph.
 * @returns {string} Etiqueta en español (o el valor crudo si es desconocido).
 */
export const getPlatformLabel = (platformType: string | null): string =>
  platformType ? PLATFORM_LABELS[platformType] || platformType : '—';

/**
 * Texto accionable del problema detectado.
 *
 * @param {PhoneNumberStatus} phoneStatus Estado leído de la configuración.
 * @returns {string} Explicación en español de por qué el número no puede enviar.
 */
export const getPhoneNumberWarning = ({ status, platformType }: PhoneNumberStatus): string => {
  if (status && status !== HEALTHY_STATUS) {
    return (
      STATUS_WARNINGS[status] ||
      `Meta reporta el número en estado ${status}; mientras no vuelva a CONNECTED no puede enviar mensajes.`
    );
  }
  if (platformType && platformType !== HEALTHY_PLATFORM_TYPE) {
    return `El número está en ${getPlatformLabel(platformType)}, no en WhatsApp Cloud API; esta integración no puede enviar mensajes con él.`;
  }
  return '';
};

/**
 * Atajo card-level: ¿la configuración persistida delata un número que NO puede enviar?
 *
 * Combina `readPhoneNumberStatus` + `isPhoneNumberHealthy` en la única pregunta que la
 * card necesita responder. Mantiene la regla del módulo: **sin dato no hay problema**
 * — si el backend todavía no persiste `status`/`platform_type`, esto devuelve `true`
 * (sano). Pintar ámbar por ausencia de dato sería una advertencia falsa para el 100%
 * de los comercios, que es peor que el hueco que tapa.
 *
 * @param {Record<string, unknown>} config Configuración persistida de la integración.
 * @returns {boolean} `true` si nada de lo conocido impide enviar.
 */
export const isConfigPhoneHealthy = (config: Record<string, unknown>): boolean => {
  const phoneStatus = readPhoneNumberStatus(config || {});
  return !phoneStatus || isPhoneNumberHealthy(phoneStatus);
};
